import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui';
import { userApi } from '@/features/users/api/user.api';
import type { MeResponse, User } from '@/types';

const schema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(50),
  lastName: z.string().trim().min(1, 'Last name is required').max(50),
  email: z.string().trim().email('Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

interface TravelerProfileSetupFormProps {
  onSuccess: (data: MeResponse) => void;
  compact?: boolean;
  /** Prefill from Google / existing user fields (not persisted until submit). */
  defaultValues?: Partial<Pick<User, 'firstName' | 'lastName' | 'email'>>;
}

export function TravelerProfileSetupForm({
  onSuccess,
  compact,
  defaultValues,
}: TravelerProfileSetupFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
      email: defaultValues?.email ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: userApi.completeProfile,
    onSuccess,
  });

  return (
    <Box>
      <Typography
        variant={compact ? 'h5' : 'h5'}
        fontWeight={compact ? 700 : 800}
        textAlign={compact ? 'center' : 'left'}
        sx={{ mb: compact ? 0.5 : 1 }}
      >
        Complete your profile
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign={compact ? 'center' : 'left'}
        sx={{ mb: compact ? 2.5 : 3, lineHeight: 1.65 }}
      >
        Tell us a bit about yourself before you continue.
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {(mutation.error as Error).message || 'Failed to save profile. Please try again.'}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit((values) => mutation.mutate(values))}>
        <TextField
          {...register('firstName')}
          label="First name"
          fullWidth
          margin={compact ? 'dense' : 'normal'}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          autoComplete="given-name"
        />
        <TextField
          {...register('lastName')}
          label="Last name"
          fullWidth
          margin={compact ? 'dense' : 'normal'}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          autoComplete="family-name"
        />
        <TextField
          {...register('email')}
          label="Email"
          type="email"
          fullWidth
          margin={compact ? 'dense' : 'normal'}
          error={!!errors.email}
          helperText={errors.email?.message}
          autoComplete="email"
        />

        <Button
          type="submit"
          intent="primary-dark"
          fullWidth
          size="large"
          sx={{ mt: compact ? 2.5 : 3 }}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
}
